// No React imports here (SSR-friendly, no DOM usage)
/**
 * Utility: ensure absolute URL
 */
const absolutize = (base, maybeRel) => {
  if (!maybeRel) return null;
  if (/^https?:\/\//i.test(maybeRel)) return maybeRel;
  // Ensure single slash join
  return `${base.replace(/\/+$/, "")}/${maybeRel.replace(/^\/+/, "")}`;
};

/**
 * Utility: safe ISO date or undefined
 */
const safeISO = (value) => {
  if (!value) return undefined;
  const d = new Date(value);
  return isNaN(d.getTime()) ? undefined : d.toISOString();
};

/**
 * Utility: tidy description to ~155 chars on word boundary
 */
const summarize = (text, max = 155) => {
  if (!text) return "";
  const t = text.trim().replace(/\s+/g, " ");
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 60 ? cut.slice(0, lastSpace) : cut) + "…";
};

/**
 * Strip custom markup/HTML -> plain text
 */
const stripMarkup = (s) =>
  s
    .replace(/\[.*?\]/g, "")     // custom shortcodes like [img:], [ul:], etc.
    .replace(/<[^>]*>/g, "")     // HTML tags
    .replace(/\s+/g, " ")
    .trim();

/**
 * Generates comprehensive SEO data for blog posts
 */
export const generateBlogSEO = (blogPost, baseUrl = "https://www.beanbag.ai") => {
  if (!blogPost) return getDefaultSEO(baseUrl);

  const {
    id,
    title = "",
    image,
    date,
    author,
    categories = [],
    sections = [],
    seo = {},
    lastModified,
  } = blogPost;

  const metaTitle = seo.metaTitle || title || "Beanbag AI";
  const blogUrl = `${baseUrl.replace(/\/+$/, "")}/blog/${encodeURIComponent(id || "")}`;

  // Description
  const metaDescription =
    seo.metaDescription ||
    (sections[0]?.content ? summarize(stripMarkup(sections[0].content), 160) :
      `Read our latest insights about ${title} on Beanbag AI blog.`);

  // Keywords (kept for non-Google engines)
  const keywords =
    (Array.isArray(seo.keywords) ? seo.keywords : [
      "AI", "artificial intelligence", "automation", "business solutions", "Beanbag AI",
      ...categories.filter((c) => c && c !== "All"),
      ...title.toLowerCase().split(" ").filter((w) => w.length > 3).slice(0, 5),
    ]).join(", ");

  // Tags (for article:tag + JSON-LD keywords)
  const articleTags = Array.from(
    new Set([
      ...categories.filter((c) => c && c !== "All"),
      ...title
        .toLowerCase()
        .split(" ")
        .filter((w) => ["ai", "voice", "automation", "shopping", "personalization", "machine", "learning"].includes(w)),
    ])
  );

  // Images (absolute)
  const ogImageUrl = absolutize(baseUrl, seo.ogImage || image) || `${baseUrl}/assets/default-blog-image.jpg`;
  const twitterImageUrl = absolutize(baseUrl, seo.twitterImage || seo.ogImage || image) || `${baseUrl}/assets/default-blog-image.jpg`;
  const authorImageUrl = author?.image ? absolutize(baseUrl, author.image) : `${baseUrl}/assets/default-author.jpg`;

  // Dates
  const publishedISO = safeISO(date);
  const modifiedISO = safeISO(lastModified) || safeISO(new Date());

  // Primary section
  const primarySection = categories[0] || "Technology";

  return {
    // Basic SEO
    title: `${metaTitle} - Beanbag AI Blog`,
    description: metaDescription,
    canonical: blogUrl,
    keywords,
    robots: "index, follow",
    author: author?.name || "Beanbag AI Team",

    // Open Graph
    ogTitle: metaTitle,
    ogDescription: metaDescription,
    ogImage: ogImageUrl,
    ogUrl: blogUrl,
    ogType: "article",

    // Article OG extras (ensure your hook writes these too)
    articlePublishedTime: publishedISO,   // -> meta property="article:published_time"
    articleModifiedTime: modifiedISO,     // -> meta property="article:modified_time"
    articleSection: primarySection,       // -> meta property="article:section"
    articleTags,                          // -> multiple meta property="article:tag" contents

    // Twitter
    twitterCard: "summary_large_image",
    twitterSite: "@BeanbagAI",
    twitterTitle: metaTitle,
    twitterDescription: metaDescription,
    twitterImage: twitterImageUrl,

    // JSON-LD
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: metaTitle,
      description: metaDescription,
      image: [{ "@type": "ImageObject", url: ogImageUrl, width: 1200, height: 630 }],
      author: {
        "@type": "Person",
        name: author?.name || "Beanbag AI Team",
        jobTitle: author?.designation || "Content Team",
        image: authorImageUrl,
      },
      publisher: {
        "@type": "Organization",
        "name": "Beanbag AI",
        "url": baseUrl,
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/logo192.png`,
          "width": 300,
          "height": 60
        }
      },
      datePublished: publishedISO,
      dateModified: modifiedISO,
      mainEntityOfPage: { "@type": "WebPage", "@id": blogUrl },
      articleSection: primarySection,
      keywords: articleTags.join(", "),
      url: blogUrl,
      isPartOf: { "@type": "Blog", "@id": `${baseUrl}/blog`, name: "Beanbag AI Blog" }
    }
  };
};

/**
 * Generates FAQ Schema (schema.org/FAQPage) for blog posts with FAQ sections
 * Optimized for Google Rich Results and AI systems (ChatGPT, Gemini, Perplexity)
 */
export const generateFAQSchema = (blogPost, baseUrl = "https://www.beanbag.ai") => {
  if (!blogPost || !blogPost.sections) return null;

  // Find FAQ section
  const faqSection = blogPost.sections.find(
    (section) => section.id === "faqs" || section.title?.toLowerCase().includes("faq")
  );

  if (!faqSection || !faqSection.content) return null;

  // Parse FAQ content - supports numbered format "1. Question?\nAnswer\n\n2. Question?\nAnswer"
  const faqContent = faqSection.content;
  const faqPairs = [];

  // Split by double newlines to get potential Q&A pairs (or just Qs and As sequences)
  const blocks = faqContent.split(/\n\n+/);

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i].trim();
    if (!block) continue;

    // Try to see if this block itself contains both Q and A (separated by newline)
    // Common format: "[b:1. Question?]\nAnswer text..."
    const firstLineEnd = block.indexOf("\n");

    if (firstLineEnd > 0) {
      // We have multiple lines in this block. 
      // Assume first line is Question, rest is Answer.
      let questionPart = block.substring(0, firstLineEnd).trim();
      let answerPart = block.substring(firstLineEnd + 1).trim();

      // Clean the question (remove [b:] and number)
      // Remove [b: ... ] wrapper if present
      questionPart = questionPart.replace(/^\[b:(.*?)\]$/, "$1");
      // Remove leading "1. " or "1) "
      questionPart = questionPart.replace(/^\d+[\.\)]\s*/, "");

      // Is it really a question?
      if (questionPart && answerPart && questionPart.includes("?")) {
        // Clean up answer
        const cleanAnswer = answerPart
          .replace(/\[link:[^\|]+\|([^\]]+)\]/g, "$1") // Extract link text
          .replace(/\[.*?\]/g, "") // Remove other markdown/custom tags
          .replace(/<[^>]*>/g, "") // Remove HTML
          .trim();

        if (cleanAnswer) {
          faqPairs.push({
            question: questionPart,
            answer: cleanAnswer
          });
          continue; // Successfully parsed this block as a pair
        }
      }
    }

    // Fallback: If block didn't match single-block Q&A, check the old logic (Q block then A block)
    // But given the current data format, the above logic should catch it. 
    // We will leave the old logic as a backup only if the block looks strictly like a question.

    let cleanBlock = block.replace(/^\[b:(.*?)\]$/, "$1").trim();
    const isQuestion = cleanBlock.match(/^(\d+[\.\)]\s*)?(.+\?)\s*$/);

    if (isQuestion && i + 1 < blocks.length) {
      const question = cleanBlock.replace(/^\d+[\.\)]\s*/, "");
      const answer = blocks[i + 1].trim();

      const cleanAnswer = answer
        .replace(/\[link:[^\|]+\|([^\]]+)\]/g, "$1")
        .replace(/\[.*?\]/g, "")
        .replace(/<[^>]*>/g, "")
        .trim();

      if (cleanAnswer) {
        faqPairs.push({ question, answer: cleanAnswer });
        i++; // Skip answer block
      }
    }
  }

  if (faqPairs.length === 0) return null;

  const blogUrl = `${baseUrl.replace(/\/+$/, "")}/blog/${encodeURIComponent(blogPost.id || "")}`;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqPairs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    })),
    url: blogUrl,
    isPartOf: {
      "@type": "BlogPosting",
      "@id": blogUrl
    }
  };
};

/**
 * Blog listing SEO
 */
export const generateBlogListingSEO = (activeCategory = "All", baseUrl = "https://www.beanbag.ai") => {
  const isFiltered = activeCategory !== "All";
  const title = isFiltered
    ? `${activeCategory} Articles - Beanbag AI Blog`
    : "Blog - Beanbag AI | AI Insights, Tutorials & Industry News";

  const description = isFiltered
    ? `Explore our latest ${activeCategory.toLowerCase()} articles and insights. Learn about AI solutions, automation, and how Beanbag AI can transform your business.`
    : "Discover the latest insights, tutorials, and news about AI, automation, and business transformation. Stay updated with Beanbag AI's expert analysis and solutions.";

  const canonical = `${baseUrl.replace(/\/+$/, "")}/blog`;
  const socialImage = `${baseUrl}/assets/blog-social-card.jpg`;

  const keywords = [
    ...(isFiltered ? [activeCategory.toLowerCase()] : []),
    "AI blog", "artificial intelligence articles", "automation insights",
    "business transformation", "machine learning tutorials", "AI solutions", "Beanbag AI"
  ].join(", ");

  return {
    title,
    description,
    canonical,
    keywords,
    robots: "index, follow",

    ogTitle: title,
    ogDescription: description,
    ogImage: socialImage,
    ogUrl: canonical,
    ogType: "website",

    twitterCard: "summary_large_image",
    twitterSite: "@BeanbagAI",
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: socialImage,

    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Beanbag AI Blog",
      description,
      url: canonical,
      publisher: {
        "@type": "Organization",
        name: "Beanbag AI",
        url: baseUrl,
        logo: {
          "@type": "ImageObject",
          url: `${baseUrl}/assets/logo192.png`,
          width: 300,
          height: 60
        }
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical }
    }
  };
};

/**
 * Default sitewide SEO
 */
export const getDefaultSEO = (baseUrl = "https://www.beanbag.ai") => {
  const canonical = baseUrl.replace(/\/+$/, "");
  return {
    title: "Beanbag AI - AI-Powered Business Automation Platform",
    description:
      "Transform your business with Beanbag AI's low-code platform. Automate workflows, boost productivity, and scale your operations with our AI solutions.",
    canonical,
    keywords:
      "AI platform, business automation, workflow automation, artificial intelligence, low-code platform",
    robots: "index, follow",

    ogTitle: "Beanbag AI - AI-Powered Business Automation",
    ogDescription: "Transform your business with AI-powered automation solutions.",
    ogImage: `${canonical}/assets/og-default.jpg`,
    ogUrl: canonical,
    ogType: "website",

    twitterCard: "summary_large_image",
    twitterSite: "@BeanbagAI",

    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Beanbag AI",
      url: canonical,
      logo: `${canonical}/assets/logo192.png`,
    },
  };
};
